import { useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { Bookmark, ChevronDown, ChevronUp, GripVertical, Plus, X } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import {
  addLink,
  addView,
  entryId,
  groupEntries,
  groupKeyFor,
  hiddenLinks,
  navLabel,
  newId,
  removeEntry,
  renameView,
  swapEntries,
  type RoleGroup,
  type SidebarEntry,
  type SidebarLayout,
} from './sidebarLayout';

export interface PendingView {
  url: string;
  defaultName: string;
}

interface SidebarCustomizerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  layout: SidebarLayout;
  roleGroup: RoleGroup | null;
  /** When set, the customizer opens with this page already added at the top, ready to name and place. */
  pendingView?: PendingView | null;
  onSave: (layout: SidebarLayout) => void;
  /** Returns the role's default layout, for "Reset to default". */
  defaultFor: () => SidebarLayout;
}

/**
 * Arrange the sidebar: reorder rows within their heading, and hide pages you
 * do not use.
 *
 * Which heading a row sits under is not up for arrangement. A page either
 * answers to the selected school year or it does not, and a saved view is a
 * saved view — so the headings are shown here the way the sidebar shows them,
 * as the shape you are ordering things inside rather than another thing to
 * drag. Nothing is saved until Save; Cancel throws the draft away.
 */
export function SidebarCustomizer({
  open, onOpenChange, layout, roleGroup, pendingView, onSave, defaultFor,
}: SidebarCustomizerProps) {
  const [draft, setDraft] = useState<SidebarLayout>(layout);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropId, setDropId] = useState<string | null>(null);
  const pendingInputRef = useRef<HTMLInputElement>(null);

  // Start every session from what is actually saved, plus the page being added.
  useEffect(() => {
    if (!open) return;
    if (pendingView) {
      const id = newId('view');
      setDraft(addView(layout, { id, name: pendingView.defaultName, url: pendingView.url }));
      setPendingId(id);
    } else {
      setDraft(layout);
      setPendingId(null);
    }
    setDragId(null);
    setDropId(null);
  }, [open, layout, pendingView]);

  const groups = useMemo(() => groupEntries(draft.entries, roleGroup), [draft, roleGroup]);
  const hidden = useMemo(() => hiddenLinks(draft, roleGroup), [draft, roleGroup]);

  const labelFor = (entry: SidebarEntry) =>
    (entry.type === 'link' ? navLabel(entry.key, roleGroup) : entry.name);

  /** Rows only trade places with their own neighbours, so a drag cannot refile anything. */
  const sameGroup = (a: string, b: string) => {
    const find = (id: string) => draft.entries.find((entry) => entryId(entry) === id);
    const first = find(a);
    const second = find(b);
    return !!first && !!second
      && groupKeyFor(first, roleGroup) === groupKeyFor(second, roleGroup);
  };

  const handleDragStart = (event: DragEvent, id: string) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', id);
    setDragId(id);
  };

  const handleDragEnd = () => {
    setDragId(null);
    setDropId(null);
  };

  const handleDragOver = (event: DragEvent<HTMLElement>, id: string) => {
    if (!dragId || dragId === id || !sameGroup(dragId, id)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDropId(id);
  };

  const handleDrop = (event: DragEvent<HTMLElement>, id: string) => {
    event.preventDefault();
    const dragged = dragId ?? event.dataTransfer.getData('text/plain');
    if (dragged && dragged !== id && sameGroup(dragged, id)) {
      setDraft((current) => swapEntries(current, dragged, id));
    }
    handleDragEnd();
  };

  const renderRow = (entry: SidebarEntry, siblings: SidebarEntry[], index: number) => {
    const id = entryId(entry);
    const isPending = entry.type === 'view' && entry.id === pendingId;
    const swapWith = (direction: -1 | 1) => {
      const neighbour = siblings[index + direction];
      if (neighbour) setDraft((current) => swapEntries(current, id, entryId(neighbour)));
    };
    return (
      <li key={id}>
        <div
          draggable
          onDragStart={(event) => handleDragStart(event, id)}
          onDragEnd={handleDragEnd}
          onDragOver={(event) => handleDragOver(event, id)}
          onDrop={(event) => handleDrop(event, id)}
          data-testid="sidebar-row"
          className={`flex items-center gap-2 rounded-md border bg-card px-2 py-1.5 ${
            dragId === id ? 'opacity-40' : ''
          } ${dropId === id ? 'border-primary' : ''} ${isPending ? 'border-primary ring-1 ring-primary' : ''}`}
        >
          <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-muted-foreground" aria-hidden="true" />
          {entry.type === 'view' ? (
            <>
              <Bookmark className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <Input
                ref={isPending ? pendingInputRef : undefined}
                value={entry.name}
                onChange={(event) => setDraft((current) => renameView(current, entry.id, event.target.value))}
                className="h-7 flex-1 text-sm"
                aria-label={`Name for ${entry.url}`}
              />
            </>
          ) : (
            <span className="flex-1 truncate text-sm">{labelFor(entry)}</span>
          )}
          <RowControls
            label={labelFor(entry)}
            canUp={index > 0}
            canDown={index < siblings.length - 1}
            onUp={() => swapWith(-1)}
            onDown={() => swapWith(1)}
            onRemove={() => setDraft((current) => removeEntry(current, id).layout)}
            removeTitle={entry.type === 'link' ? 'Hide' : 'Remove'}
          />
        </div>
      </li>
    );
  };

  const save = () => {
    onSave(draft);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[85vh] max-w-lg flex-col"
        onOpenAutoFocus={(event) => {
          // Land in the name field when a page is being added, so typing renames it straight away.
          if (pendingInputRef.current) {
            event.preventDefault();
            pendingInputRef.current.focus();
            pendingInputRef.current.select();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{pendingView ? 'Add this page to your sidebar' : 'Customize sidebar'}</DialogTitle>
          <DialogDescription>
            Drag a row or use the arrows to reorder it. Pages stay under the heading they belong to.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          {groups.map((group) => (
            <div key={group.key}>
              {group.label && (
                <h3 className="px-0.5 pb-1.5 text-sm font-medium text-foreground">{group.label}</h3>
              )}
              <ul className="space-y-1" aria-label={group.label ?? 'Sidebar items'}>
                {group.entries.map((entry, index) => renderRow(entry, group.entries, index))}
              </ul>
            </div>
          ))}

          {hidden.length > 0 && (
            <div className="space-y-2 border-t pt-3">
              <p className="text-sm font-medium text-foreground">Hidden pages</p>
              <div className="flex flex-wrap gap-2">
                {hidden.map((key) => (
                  <Button
                    key={key}
                    variant="secondary"
                    size="sm"
                    className="h-7 gap-1 px-2.5"
                    onClick={() => setDraft((current) => addLink(current, key))}
                    aria-label={`Show ${navLabel(key, roleGroup)}`}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {navLabel(key, roleGroup)}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-row items-center justify-between gap-2 sm:justify-between">
          <Button variant="ghost" size="sm" onClick={() => setDraft(defaultFor())}>
            Reset to default
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={save}>Save</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RowControlsProps {
  label: string;
  canUp: boolean;
  canDown: boolean;
  onUp: () => void;
  onDown: () => void;
  onRemove: () => void;
  removeTitle: string;
}

function RowControls({ label, canUp, canDown, onUp, onDown, onRemove, removeTitle }: RowControlsProps) {
  return (
    <div className="flex shrink-0 items-center">
      <Button variant="ghost" size="icon" className="h-7 w-7" disabled={!canUp} onClick={onUp} aria-label={`Move ${label} up`}>
        <ChevronUp className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7" disabled={!canDown} onClick={onDown} aria-label={`Move ${label} down`}>
        <ChevronDown className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={onRemove} aria-label={`${removeTitle} ${label}`}>
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default SidebarCustomizer;
